import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './sic-carousel.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';

import Splide from '@splidejs/splide';

const elementName = 'sic-carousel';

// This is a modified version of the material outline `chevron right`. It was scaled and flattened. Splide requires it to be sized to 40 x 40. You can export from Figma to get a flattened SVG (without transform attribute). To resize, you can use https://editor.method.ac/.
const arrowPath = `m28.12733,20.97375l-13.49524,13.2891c-0.53843,0.5384 -1.4091,0.5384 -1.94753,0l-0.81339,-0.8134c-0.53843,-0.5384 -0.53843,-1.4091 0,-1.9475l11.71956,-11.5019l-11.7081,-11.50196c-0.53844,-0.53843 -0.53844,-1.4091 0,-1.94753l0.81338,-0.81339c0.53844,-0.53843 1.4091,-0.53843 1.94754,0l13.49528,13.28908c0.527,0.5384 0.527,1.4091 -0.0115,1.9475z`;

/**
 * The SIC carousel component
 *
 * @element sic-carousel
 * @slot slide
 */
@customElement(elementName)
export class SicCarousel extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  private _splide: Splide;

  observer: MutationObserver;

  @state() currentSlide: number;

  @state() totalSlides: number;

  @property({
    type: String,
    attribute: 'label',
  })
  labelText = 'carousel';

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'has-prev',
  })
  hasPrev: boolean;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'has-next',
  })
  hasNext: boolean;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'slide-first',
  })
  slideFirst: boolean;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'slide-last',
  })
  slideLast: boolean;

  render(): TemplateResult {
    return html`
      <div class="carousel-wrapper">
        <div></div>
        <div class="carousel splide">
          <div class="carousel-inner">
            <div class="splide__wrapper">
              <div
                class="splide__counter"
                aria-live="polite"
                aria-atomic="true"
              >
                <span class="current-slide"
                  >${this.currentSlide}<span class="current-slide--dash">-</span
                  ><span class="current-slide--lg"
                    >${this.currentSlide + 1}</span
                  ><span class="current-slide--xl"
                    >${this.currentSlide + 2}</span
                  ></span
                >
                <span class="visually-hidden"> of </span>
                <span class="counter-separator" aria-hidden="true"> / </span>
                <span class="total-slides">${this.totalSlides}</span>
              </div>
              <div class="splide__track">
                <div class="splide__list">${this.imageListTemplate()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();

    this.observer = new MutationObserver(this.setSlots);
    this.observer.observe(this, { childList: true });
  }

  disconnectedCallback() {
    this.observer.disconnect();
  }

  firstUpdated() {
    this._splide = new Splide(
      this.shadowRoot?.querySelector('.carousel') as HTMLElement,
      {
        slideFocus: false,
        autoHeight: true,
        keyboard: false,
        perPage: 1,
        rewind: false,
        pagination: false,
        arrowPath: arrowPath,
        perMove: 1,
        gap: '2rem',
        mediaQuery: 'min',
        label: this.labelText,
        padding: {
          right: '6rem',
        },
        live: false,
        // These are desktop first? Page refresh can be different than resizing. Not the same as Tailwind.
        // TODO: update hard coded breakpoint values to use (screenSizeMap.get('lg') as number).
        breakpoints: {
          1024: {
            perPage: 2,
            padding: {
              left: '0rem',
              right: '50%',
            },
          },
          1380: {
            perPage: 3,
            padding: {
              left: '0rem',
              right: '25%',
            },
          },
          1920: {
            perPage: 3,
            padding: {
              left: '0rem',
              right: '20%',
            },
          },
        },
      }
    );

    this._splide.mount();

    this.setSlots();

    this._splide.on('moved', () => {
      setTimeout(() => {
        this.requestUpdate();
      }, 10);
    });

    // These are injected by Splide, so we wait to modify them.
    const arrowPrev = this.shadowRoot?.querySelector(
      '.splide__arrow--prev'
    ) as HTMLElement;
    const arrowNext = this.shadowRoot?.querySelector(
      '.splide__arrow--next'
    ) as HTMLElement;

    if (arrowPrev) {
      arrowPrev.setAttribute(
        'aria-label',
        `Previous Slide for ${this.labelText}`
      );
      arrowPrev.innerHTML =
        arrowPrev.innerHTML +
        '<span class="screen-reader-only">Previous Slide</span>';
    }

    if (arrowNext) {
      arrowNext.setAttribute('aria-label', `Next Slide for ${this.labelText}`);
      arrowNext.innerHTML =
        arrowNext.innerHTML +
        '<span class="screen-reader-only">Next Slide</span>';
    }

    // Manipulates the DOM by detaching the splide
    // arrows and appending it to the end of the carousel.
    let arrows = this.shadowRoot?.querySelector('.splide__arrows');

    if (arrows) {
      arrows = arrows.parentNode?.removeChild(arrows);
      arrows &&
        this.shadowRoot?.querySelector('.splide__wrapper')?.prepend(arrows);
    }
  }

  updated(): void {
    this.setFadePosition();

    const showCounter = this.shadowRoot?.querySelector(
      '.splide__counter'
    ) as HTMLElement;

    if (showCounter) {
      this.currentSlide = this._splide.index + 1;
      this.totalSlides = this._splide.length;
    }
  }

  setSlots = () => {
    let counter = 0;
    while (counter <= this.childElementCount) {
      if (this.children[counter]) {
        this.children[counter].setAttribute('slot', `slide-${counter + 1}`);
      }
      counter++;
    }
    this.requestUpdate();
  };

  imageListTemplate = (): TemplateResult[] => {
    let counter = 0;
    const templateArr: TemplateResult[] = [];

    while (counter < this.childElementCount) {
      templateArr.push(
        html` <div
          class="splide__slide"
          role="group"
          aria-label="slide ${counter + 1} of ${this.childElementCount}"
        >
          <slot name="slide-${counter + 1}"></slot>
          <div aria-hidden="true" class="slide-count">
            <span>${counter + 1} / ${this.childElementCount}</span>
          </div>
        </div>`
      );

      counter++;
    }

    return templateArr;
  };

  /* Updated to current API for SplideJS 4 from
   * `const scrollLength = this._splide.Components.Controller.getEnd();`
   * `const slideIndex = this._splide.index + 1;`
   * may cause regressions on the fade.
   */

  setFadePosition = () => {
    const scrollLength = this._splide.length + 1;
    const slideIndex = this._splide.index;

    this.hasPrev = slideIndex > 0;
    this.hasNext = scrollLength !== slideIndex;
    this.slideFirst = slideIndex == 0;
    this.slideLast =
      this._splide.Components.Controller.getNext() == -1 &&
      this._splide.length > 1;
  };
}
